import { useCallback, useEffect } from 'react';
import { computePositionRect, POSITION_TRANSITION, type PositionInput, useChatWidgetPositionStore } from './store';
import useElement from './useElement';

// Match AgentChatPanel positioning (`right-4 bottom-24` → 16px / 96px) so the
// Intercom messenger and the scheduling agent panel sit in the same spot above
// the AIChatFloatingButton FAB.
const INTERCOM_HORIZONTAL_PADDING = 16;
const INTERCOM_VERTICAL_PADDING = 96;

const setElementPosition = (el: HTMLElement | null, position: PositionInput, isFooterVisible: boolean) => {
  if (!el) return;
  const { right, bottom } = computePositionRect(position, isFooterVisible);
  el.style.transition = POSITION_TRANSITION;
  el.style.setProperty('right', `${right}px`, 'important');
  el.style.setProperty('bottom', `${bottom}px`, 'important');
};

const useChatWidget = () => {
  const { element: hubspotElement } = useElement('#hubspot-messages-iframe-container');
  const isFooterVisible = useChatWidgetPositionStore((s) => s.isFooterVisible);
  const storePosition = useChatWidgetPositionStore((s) => s.position);
  const setStorePosition = useChatWidgetPositionStore((s) => s.setPosition);

  // Keep HubSpot aligned with the current store position/footer via DOM styles,
  // and keep the Intercom messenger pinned above the AIChatFloatingButton FAB.
  useEffect(() => {
    setElementPosition(hubspotElement, storePosition, isFooterVisible);
    window.Intercom?.('update', {
      horizontal_padding: INTERCOM_HORIZONTAL_PADDING,
      vertical_padding: INTERCOM_VERTICAL_PADDING,
    });
  }, [hubspotElement, storePosition, isFooterVisible]);

  const setPosition = useCallback(
    (position: PositionInput) => {
      setStorePosition(position);
    },
    [setStorePosition]
  );

  const hide = useCallback(
    (hideIt: boolean) => {
      if (hubspotElement) {
        hubspotElement.style.setProperty('display', hideIt ? 'none' : 'initial', 'important');
      }
      // Intercom's default launcher stays hidden always — the AIChatFloatingButton FAB
      // is the only entry point. We only collapse the messenger panel when hiding.
      if (hideIt) window.Intercom?.('hide');
    },
    [hubspotElement]
  );

  const openIntercom = useCallback(() => {
    window.Intercom?.('show');
  }, []);

  const closeIntercom = useCallback(() => {
    window.Intercom?.('hide');
  }, []);

  const sendIntercomMessage = useCallback((message: string) => {
    window.Intercom?.('showNewMessage', message);
  }, []);

  return {
    setPosition,
    hide,
    openIntercom,
    closeIntercom,
    sendIntercomMessage,
    hubspotElement,
    isFooterVisible,
  };
};

export default useChatWidget;
